/*
 * The field-by-field form.
 *
 * One question fills the frame. The point of the shape is that the person is
 * only ever looking at one decision, so everything here is in service of that:
 * the question is display-sized, the field is the only interactive thing at
 * rest, and the chrome is small and out of the way at the top and bottom.
 */

.flow {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-8);
  min-height: 28rem;
  position: relative;
}

/*
 * The welcome screen.
 *
 * Arrives with the same rise as a step, because it is the first screen of the
 * same flow rather than a different kind of page. It carries no progress strip,
 * so it does not need the strip's height reserved above it.
 */
.welcome {
  animation: stepIn var(--gb-motion-slow) var(--gb-ease-out) both;
}

@media (prefers-reduced-motion: reduce) {
  .welcome {
    animation: none;
  }
}

/* --- Progress ----------------------------------------------------------- */

/*
 * The progress strip.
 *
 * The guidelines' signature device, doing the job it already means: this got
 * further, and we measured it. A generic percentage bar would have been the one
 * place in the product where progress is drawn in a way that means nothing.
 */
.progress {
  display: flex;
  /* Stretch rather than flex-end: each entry is a full-height hit area now, and
     the bar inside it is what sits on the floor. See `.progressStep`. */
  align-items: stretch;
  gap: 3px;
  height: 1.5rem;
}

/*
 * One entry: the way back to that question, and the hit area for it.
 *
 * The strip already said which question you are on and how many are behind you,
 * and pointing at the one you wanted did nothing — so the way back to the third
 * of seven was Back, Back, Back, or the whole way forward to the review screen
 * where every answer finally had a `Change` beside it.
 *
 * The bar is 35% of a 1.5rem strip at rest, which is about eight pixels: a
 * target nobody could hit. So the button is the full height of the strip and
 * transparent, and `.progressBar` inside it is the part that is drawn.
 */
.progressStep {
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-end;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
}

/* A question you have not reached yet is not somewhere you may skip to. */
.progressStep:disabled {
  cursor: default;
}

.progressBar {
  width: 100%;
  background: var(--gb-ink-15);
  height: 35%;
  transition:
    height var(--gb-motion-slow) var(--gb-ease),
    background var(--gb-motion-slow) var(--gb-ease);
}

.progressBar[data-state='done'] {
  background: var(--gb-accent);
  height: 70%;
}

.progressBar[data-state='current'] {
  background: var(--gb-accent);
  height: 100%;
}

/*
 * Hovering a step you can return to raises it to where it would be if you were
 * standing on it. Nothing else in the strip moves, so the gesture reads as
 * *this is the one you would land on* rather than as decoration.
 */
.progressStep:hover:not(:disabled) .progressBar,
.progressStep:focus-visible:not(:disabled) .progressBar {
  height: 100%;
}

.progressStep:focus-visible {
  outline: var(--gb-rule-width) solid var(--gb-accent);
  outline-offset: 2px;
}

.progressHead {
  display: flex;
  justify-content: space-between;
  /* Centre rather than baseline: Back is a boxed control now, and a border
     aligned on the count's baseline sits low enough to look like a mistake. */
  align-items: center;
  gap: var(--gb-space-4);
}

.progressCount {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-muted);
}

/* --- The step ----------------------------------------------------------- */

/*
 * The step fills the column it is given.
 *
 * It used to be capped at 34rem, which on any normal monitor left the question
 * set in a narrow strip down the left with the rest of the page empty beside
 * it, and wrapped short questions onto three lines for no reason. The page
 * shell already decides the measure — the flow does not need a second, smaller
 * opinion about it.
 */
.step {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-6);
  flex: 1;
}

/*
 * Steps enter from below and leave upward, which is the direction the form is
 * travelling. Keyed on the step name in React so each one animates in rather
 * than the text swapping in place.
 */
.stepEnter {
  animation: stepIn var(--gb-motion-slow) var(--gb-ease-out) both;
}

.stepEnterBack {
  animation: stepInBack var(--gb-motion-slow) var(--gb-ease-out) both;
}

@keyframes stepIn {
  from {
    opacity: 0;
    transform: translateY(0.75rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

@keyframes stepInBack {
  from {
    opacity: 0;
    transform: translateY(-0.75rem);
  }
  to {
    opacity: 1;
    transform: none;
  }
}

/*
 * The question, full width.
 *
 * The 18ch cap that used to be here broke "Where is the studio registered?"
 * across three lines inside the left half of the screen. A question is the
 * loudest thing on its screen and there is nothing beside it to protect, so it
 * gets the whole column; only running prose needs a reading measure, which is
 * why `.help` below keeps one and this does not.
 */
.question {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 118,
    'wght' 800;
  text-transform: uppercase;
  font-size: clamp(1.5rem, 1.1rem + 1.8vw, 2.25rem);
  line-height: 1;
  letter-spacing: -0.01em;
  margin: 0;
}

/*
 * The sentence under the question, running the column the question runs.
 *
 * It was capped at 46 characters while `.question` above it deliberately took
 * the whole width — so every step had a full-width heading with a narrow strip
 * of grey text under its left-hand end, which reads as a paragraph that failed
 * to load rather than as a measure anybody chose. The page shell already
 * decides how wide this column is.
 */
.help {
  color: var(--gb-text-muted);
  margin: 0;
}

.hint {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  color: var(--gb-text-muted);
  margin: 0;
  word-break: break-all;
}

/* --- Fields ------------------------------------------------------------- */

/*
 * Big, and underlined rather than boxed.
 *
 * A boxed input at this size reads as a form; a rule under a large answer reads
 * as writing. The rule thickens and takes the accent on focus, which is the
 * only colour change in the field.
 */
.field {
  display: flex;
  align-items: baseline;
  gap: 0.15em;
  border-bottom: 2px solid var(--gb-rule);
  padding-bottom: var(--gb-space-2);
  transition: border-color var(--gb-motion) var(--gb-ease);
}

.field:focus-within {
  border-bottom-color: var(--gb-accent);
}

.field[data-invalid='true'] {
  border-bottom-color: var(--gb-accent);
}

.affix {
  font-family: var(--gb-font-mono);
  font-size: 1rem;
  color: var(--gb-text-faint);
  white-space: nowrap;
}

.input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 0;
  font-family: var(--gb-font-text);
  font-size: clamp(1.125rem, 1rem + 0.6vw, 1.5rem);
  line-height: 1.3;
  color: var(--gb-text);
}

.input:focus {
  outline: none;
}

.input::placeholder {
  color: var(--gb-text-faint);
}

.inputMono {
  font-family: var(--gb-font-mono);
  font-variant-numeric: tabular-nums;
  font-feature-settings: 'tnum' 1;
}

.textarea {
  composes: input;
  resize: vertical;
  min-height: 6rem;
}

/* --- Choice list -------------------------------------------------------- */

.choice {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-3);
}

.choiceList {
  list-style: none;
  margin: 0;
  padding: 0;
  max-height: 15rem;
  overflow-y: auto;
  border: var(--gb-rule-width) solid var(--gb-rule);
}

.choiceItem {
  display: flex;
  width: 100%;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--gb-space-4);
  background: transparent;
  border: 0;
  border-bottom: var(--gb-rule-width) solid var(--gb-ink-07);
  padding: var(--gb-space-3) var(--gb-space-4);
  /* `width: 100%` under content-box put the row a full rem past each end of the
     list, and `.choiceList` scrolls (`overflow-y: auto` makes the other axis
     `auto` too), so picking a country meant a horizontal scrollbar under it. */
  box-sizing: border-box;
  font-family: var(--gb-font-text);
  font-size: var(--gb-body);
  color: var(--gb-text);
  text-align: left;
  cursor: pointer;
}

.choiceItem:last-child {
  border-bottom: 0;
}

.choiceItem:hover,
.choiceItem:focus-visible {
  background: var(--gb-surface-sunken);
  outline: none;
}

.choiceItem[aria-selected='true'] {
  background: var(--gb-accent);
  color: var(--gb-on-accent);
}

.choiceCode {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  opacity: 0.7;
}

/* The country the request appears to come from, offered before the list. */
.detected {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
  margin: 0;
}

/*
 * What the flow has established: what a connect step proved, and the country
 * sign-up guessed before it asked.
 *
 * A rule down the accent side rather than a panel: this is a fact the flow has
 * established, in the same register as the country it detected above, and a
 * boxed success message on a screen with one thing on it reads as an alert.
 */
.connected {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-body);
  color: var(--gb-text);
  border-left: 2px solid var(--gb-accent);
  padding: var(--gb-space-2) 0 var(--gb-space-2) var(--gb-space-4);
  margin: 0;
  word-break: break-word;
}

/* --- Cards -------------------------------------------------------------- */

.cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(12rem, 1fr));
  gap: var(--gb-space-3);
}

.card {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-1);
  text-align: left;
  background: var(--gb-surface-raised);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: var(--gb-space-4);
  cursor: pointer;
  color: var(--gb-text);
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    transform var(--gb-motion) var(--gb-ease);
}

.card:hover {
  border-color: var(--gb-accent);
  transform: translateY(-2px);
}

.card[aria-pressed='true'] {
  border-color: var(--gb-accent);
  box-shadow: inset 0 0 0 1px var(--gb-accent);
}

.cardLabel {
  font-family: var(--gb-font-display);
  font-variation-settings:
    'wdth' 112,
    'wght' 800;
  text-transform: uppercase;
  font-size: 1rem;
  line-height: 1.1;
}

.cardDescription {
  font-size: 0.875rem;
  color: var(--gb-text-muted);
}

/* --- Controls ----------------------------------------------------------- */

.controls {
  display: flex;
  align-items: center;
  gap: var(--gb-space-4);
  flex-wrap: wrap;
  margin-top: auto;
}

.next {
  font-family: var(--gb-font-text);
  font-size: var(--gb-ui-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  background: var(--gb-accent);
  color: var(--gb-on-accent);
  border: 0;
  border-radius: var(--gb-radius);
  padding: var(--gb-space-3) var(--gb-space-6);
  cursor: pointer;
  transition: transform var(--gb-motion) var(--gb-ease);
}

.next:hover:not(:disabled) {
  transform: translateY(-1px);
}

.next:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.ghost {
  background: transparent;
  border: 0;
  padding: var(--gb-space-2) 0;
  font-family: var(--gb-font-text);
  font-size: var(--gb-ui-label);
  color: var(--gb-text-muted);
  text-decoration: underline;
  text-underline-offset: 0.25em;
  cursor: pointer;
}

.ghost:hover {
  color: var(--gb-text);
}

/*
 * Back, which used to be a `ghost`.
 *
 * A muted underlined word, set at label size, in the corner above a
 * display-sized question — the least visible thing on a screen whose whole
 * point is that there is one thing to look at. It is the second most used
 * control in the flow after Next, and people were not finding it: correcting
 * the answer you have just given is the ordinary case in a form that shows one
 * answer at a time.
 *
 * A box rather than more colour. The accent is Next's, and a Back that competed
 * with it for weight would be a form arguing with itself about which way it is
 * going — this reads as a control instead of as a footnote, takes the accent on
 * hover, and leaves the filled button as the only accent at rest.
 */
.back {
  display: inline-flex;
  align-items: center;
  gap: var(--gb-space-2);
  background: transparent;
  border: var(--gb-rule-width) solid var(--gb-rule);
  border-radius: var(--gb-radius);
  padding: var(--gb-space-2) var(--gb-space-3);
  font-family: var(--gb-font-text);
  font-size: var(--gb-ui-label);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--gb-text);
  cursor: pointer;
  transition:
    border-color var(--gb-motion) var(--gb-ease),
    color var(--gb-motion) var(--gb-ease);
}

.back:hover,
.back:focus-visible {
  border-color: var(--gb-accent);
  color: var(--gb-accent);
}

/*
 * Change, on a question that arrived already answered.
 *
 * The same box as Back, for the reason Back is a box: this is the control that
 * has to be found by the one person the answer above it is wrong for, and a
 * muted underlined word under a display-sized heading is what they miss. The
 * `ghost` on the review screen can be quiet because that screen is a list of
 * every answer at once and the eye is already scanning it; here there is one
 * thing on the page and one way to disagree with it.
 *
 * It does not stretch. The step lays its parts out in a column, and a control
 * as wide as the question would read as the primary action on a screen whose
 * primary action is Next.
 */
.change {
  composes: back;
  align-self: flex-start;
}

/*
 * The key, on the control it works.
 *
 * Escape goes back from anywhere in a step, and a shortcut nobody is told about
 * is a shortcut nobody uses — `Press Enter ↵` beside Next is the same argument
 * already made once in this file. Printed on the button rather than beside it,
 * because the two are one fact: this is what that does.
 *
 * Hidden where there is no key to press, exactly as `.enterHint` is.
 */
.backKey {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: none;
  color: var(--gb-text-faint);
  border: var(--gb-rule-width) solid var(--gb-rule);
  padding: 0 0.4em;
}

@media (hover: none) {
  .backKey {
    display: none;
  }
}

/* "Press Enter". Hidden on touch, where there is no Enter key to press. */
.enterHint {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

@media (hover: none) {
  .enterHint {
    display: none;
  }
}

.error {
  color: var(--gb-accent);
  margin: 0;
  font-size: var(--gb-body);
}

/* --- Review ------------------------------------------------------------- */

.review {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
}

.reviewList {
  list-style: none;
  margin: 0;
  padding: 0;
  border-top: var(--gb-rule-width) solid var(--gb-rule);
}

.reviewRow {
  display: grid;
  grid-template-columns: minmax(8rem, 12rem) 1fr auto;
  gap: var(--gb-space-4);
  align-items: baseline;
  padding: var(--gb-space-3) 0;
  border-bottom: var(--gb-rule-width) solid var(--gb-ink-07);
}

.reviewLabel {
  font-family: var(--gb-font-mono);
  font-size: var(--gb-data-label);
  letter-spacing: var(--gb-tracking-data);
  text-transform: uppercase;
  color: var(--gb-text-faint);
}

.reviewValue {
  word-break: break-word;
}

.reviewEmpty {
  color: var(--gb-text-faint);
  font-style: italic;
}

/* --- Done --------------------------------------------------------------- */

.done {
  display: flex;
  flex-direction: column;
  gap: var(--gb-space-4);
  animation: stepIn var(--gb-motion-slow) var(--gb-ease-out) both;
}

/*
 * Nothing above is load-bearing for comprehension, so all of it is safe to
 * switch off. The step still changes; it just does not travel.
 */
@media (prefers-reduced-motion: reduce) {
  .stepEnter,
  .stepEnterBack,
  .done {
    animation: none;
  }

  .card:hover,
  .next:hover:not(:disabled) {
    transform: none;
  }

  .progressBar,
  .back {
    transition: none;
  }
}
